import {
  CMS_DATA,
  WEBFLOW,
  INDUSTRIES,
  COMPANY_SIZES,
  WEBFLOW_ANNUAL_MAINTENANCE,
  AZAI_HOURLY_RATE,
  AZAI_HOURS_PER_STATIC_PAGE,
  AZAI_HOURS_PER_DYNAMIC_PAGE,
  REDESIGN_BASE_HOURS,
  REDESIGN_HOURS_PER_PAGE,
  REDESIGN_DEV_MULTIPLIER,
  SEO_BASE_HOURS,
  SEO_HOURS_PER_PAGE,
  OTHER_AGENCY_HOURLY_RATE,
  OTHER_AGENCY_BUILD_MULTIPLIER,
  OTHER_AGENCY_SEO_MULTIPLIER,
  FREELANCER_HOURLY_RATE,
  FREELANCER_BUILD_MULTIPLIER,
  FREELANCER_SEO_MULTIPLIER,
  DEV_HOURLY_RATE_BY_SIZE,
  getCmsLicensing,
  getCmsPerSiteCost,
  getCmsHosting,
  getCmsMaintenanceHours,
  getWebflowLicensing,
  getWebflowMaintenanceHours,
} from '../data/index.js';

export function calculateProjectCosts({
  currentCMS,
  industry,
  companySize,
  staticPages,
  dynamicPages,
  needsSeoAudit,
  needsRedesign,
  inHouseRolesOverride = null,
  benefitsMultiplier = null,
}) {
  const cms = CMS_DATA[currentCMS];
  const industryData = INDUSTRIES[industry];
  const companySizeData = COMPANY_SIZES[companySize];
  const totalPages = staticPages + dynamicPages;

  // AZAI PROJECT COST ESTIMATION
  const azaiStaticHours = staticPages * AZAI_HOURS_PER_STATIC_PAGE * industryData.multiplier;
  const azaiDynamicHours = dynamicPages * AZAI_HOURS_PER_DYNAMIC_PAGE * industryData.multiplier;
  const azaiDevHours = azaiStaticHours + azaiDynamicHours;

  let totalProjectHours;
  let redesignHours = 0;
  if (needsRedesign) {
    redesignHours = REDESIGN_BASE_HOURS + (totalPages * REDESIGN_HOURS_PER_PAGE);
    const redesignDevHours = azaiDevHours * REDESIGN_DEV_MULTIPLIER;
    totalProjectHours = redesignHours + redesignDevHours;
  } else {
    totalProjectHours = azaiDevHours;
  }

  const baseProjectCost = totalProjectHours * AZAI_HOURLY_RATE;
  const azaiProjectCost = Math.round(baseProjectCost * companySizeData.projectMultiplier);

  const seoHours = SEO_BASE_HOURS + (totalPages * SEO_HOURS_PER_PAGE);
  const baseSeoCost = seoHours * AZAI_HOURLY_RATE;
  const azaiSeoCost = needsSeoAudit ? Math.round(baseSeoCost * companySizeData.projectMultiplier) : 0;

  const azaiOneTimeTotal = azaiProjectCost + azaiSeoCost;

  // Webflow platform cost - now tiered by company size
  const isEnterpriseSize = companySize === 'enterprise' || companySize === 'largeEnterprise';
  const webflowAnnual = getWebflowLicensing(companySize);
  const webflowMonthly = Math.round(webflowAnnual / 12);

  // AOR PRICING
  const aorMonthly = companySizeData.aorMonthly;
  const aorAnnual = aorMonthly * 12;
  const aorName = companySizeData.aorName;

  const baseInHouseData = companySizeData.inHouseComparison;
  const derivedBenefitsMultiplier =
    baseInHouseData?.totalBase && baseInHouseData?.withBenefits
      ? (baseInHouseData.withBenefits / baseInHouseData.totalBase)
      : 1.3;

  const effectiveBenefitsMultiplier =
    benefitsMultiplier ??
    companySizeData.benefitsMultiplier ??
    derivedBenefitsMultiplier;

  // Optional: allow UI to override which in-house roles are selected.
  // inHouseRolesOverride can be an array of numbers (salaries) or objects { salary }.
  const overrideBaseTotal = Array.isArray(inHouseRolesOverride) && inHouseRolesOverride.length > 0
    ? inHouseRolesOverride.reduce((sum, r) => {
      if (typeof r === 'number') return sum + r;
      if (r && typeof r.salary === 'number') return sum + r.salary;
      return sum;
    }, 0)
    : null;

  const inHouseTotalBase = overrideBaseTotal ?? baseInHouseData.totalBase;
  const inHouseAnnualCost =
    overrideBaseTotal !== null
      ? Math.round(inHouseTotalBase * effectiveBenefitsMultiplier)
      : baseInHouseData.withBenefits;

  const inHouseData = {
    ...baseInHouseData,
    totalBase: inHouseTotalBase,
    withBenefits: inHouseAnnualCost,
    benefitsMultiplier: effectiveBenefitsMultiplier,
    isOverride: overrideBaseTotal !== null,
  };

  const aorSavingsVsInHouse = inHouseAnnualCost - aorAnnual;
  const aorSavingsPercent = inHouseAnnualCost > 0
    ? Math.round((aorSavingsVsInHouse / inHouseAnnualCost) * 100)
    : 0;

  // BUILD TIME CALCULATIONS
  const baseWeeksMultiplier = industryData.multiplier * (1 + Math.log10(Math.max(totalPages, 1) + 1) / 3);
  const redesignWeeksMultiplier = needsRedesign ? 1.75 : 1.0;
  const seoWeeksAddition = needsSeoAudit ? Math.max(1, Math.round(totalPages / 25)) : 0;
  const minBuildWeeks = 2;

  // COMPETITOR COMPARISONS
  const otherAgencyHours = totalProjectHours * OTHER_AGENCY_BUILD_MULTIPLIER;
  const otherAgencySeoCost = needsSeoAudit ? Math.round(seoHours * OTHER_AGENCY_SEO_MULTIPLIER * OTHER_AGENCY_HOURLY_RATE) : 0;
  const otherAgencyCost = Math.round(otherAgencyHours * OTHER_AGENCY_HOURLY_RATE) + otherAgencySeoCost;
  const otherAgencyWeeks = Math.max(minBuildWeeks * 2, Math.round(WEBFLOW.avgBuildWeeks * OTHER_AGENCY_BUILD_MULTIPLIER * baseWeeksMultiplier * redesignWeeksMultiplier) + Math.round(seoWeeksAddition * OTHER_AGENCY_SEO_MULTIPLIER));

  const freelancerHours = totalProjectHours * FREELANCER_BUILD_MULTIPLIER;
  const freelancerSeoCost = needsSeoAudit ? Math.round(seoHours * FREELANCER_SEO_MULTIPLIER * FREELANCER_HOURLY_RATE) : 0;
  const freelancerCost = Math.round(freelancerHours * FREELANCER_HOURLY_RATE) + freelancerSeoCost;
  const freelancerWeeks = Math.max(minBuildWeeks * 3, Math.round(WEBFLOW.avgBuildWeeks * FREELANCER_BUILD_MULTIPLIER * baseWeeksMultiplier * redesignWeeksMultiplier) + Math.round(seoWeeksAddition * FREELANCER_SEO_MULTIPLIER));

  const azaiBuildWeeks = Math.max(minBuildWeeks, Math.round(WEBFLOW.avgBuildWeeks * baseWeeksMultiplier * redesignWeeksMultiplier) + seoWeeksAddition);

  // TIME & OPPORTUNITY COST
  const weeksSavedVsOtherAgency = otherAgencyWeeks - azaiBuildWeeks;
  const weeksSavedVsFreelancer = freelancerWeeks - azaiBuildWeeks;
  const weeklyOpportunityCost = companySizeData.weeklyOpportunityCost;
  const opportunityCostSaved = weeksSavedVsOtherAgency * weeklyOpportunityCost;

  // LEGACY CMS COMPARISON - now using tiered pricing by company size
  const devHourlyRate = DEV_HOURLY_RATE_BY_SIZE[companySize] || DEV_HOURLY_RATE_BY_SIZE.midmarket;
  
  // Get tiered CMS costs based on company size
  const currentLicensing = getCmsLicensing(cms, companySize) + getCmsPerSiteCost(cms, companySize);
  const currentHosting = getCmsHosting(cms, companySize);
  const currentAnnualCost = currentLicensing + currentHosting;
  
  // Maintenance costs using tiered hours and tiered dev rates
  const currentMaintenanceHoursPerMonth = getCmsMaintenanceHours(cms, companySize);
  const currentMaintenanceHours = currentMaintenanceHoursPerMonth * 12;
  const currentMaintenanceCost = currentMaintenanceHours * devHourlyRate;
  const currentTotalAnnualCost = currentAnnualCost + currentMaintenanceCost;

  const currentCmsChangeTime = cms.category === 'Website Builder' ? 'Minutes' : cms.category === 'Enterprise DXP' ? '1-2 weeks' : '3-5 days';
  const webflowChangeTime = 'Minutes';

  const currentCmsBuildWeeks = cms.avgBuildWeeks;
  
  // Webflow maintenance using tiered hours and dev rates
  const webflowMaintenanceHoursPerMonth = getWebflowMaintenanceHours(companySize);
  const webflowMaintenanceCost = webflowMaintenanceHoursPerMonth * 12 * devHourlyRate;
  const webflowTotalAnnual = webflowAnnual + webflowMaintenanceCost;
  const annualPlatformSavings = currentTotalAnnualCost - webflowTotalAnnual;

  // ROI projection (Azai migration + AOR vs in-house team).
  // Note: we model the one-time migration cost at month 0.
  const inHouseMonthly = inHouseAnnualCost / 12;
  const aorMonthlyCost = aorMonthly;
  const oneTimeAzai = azaiOneTimeTotal;
  const maxMonths = 36;

  let breakEvenMonth = null;
  if (inHouseMonthly > aorMonthlyCost) {
    const raw = oneTimeAzai / (inHouseMonthly - aorMonthlyCost);
    if (Number.isFinite(raw) && raw >= 0) breakEvenMonth = Math.ceil(raw);
  }

  const roiYearly = Array.from({ length: 4 }, (_, y) => {
    const monthsAt = y * 12;
    const inHouse = Math.round(inHouseMonthly * monthsAt);
    const azai = Math.round(oneTimeAzai + (aorMonthlyCost * monthsAt));
    return { year: y, inHouse, azai, savings: inHouse - azai };
  });

  const roiPoints = [];
  for (let m = 0; m <= maxMonths; m += 3) {
    roiPoints.push({
      month: m,
      inHouse: Math.round(inHouseMonthly * m),
      azai: Math.round(oneTimeAzai + (aorMonthlyCost * m)),
    });
  }

  return {
    project: {
      azaiCost: azaiProjectCost,
      azaiSeo: azaiSeoCost,
      azaiTotal: azaiOneTimeTotal,
      azaiBuildWeeks,
      azaiHours: Math.round(totalProjectHours),
      redesignHours: needsRedesign ? Math.round(redesignHours) : 0,
      devHours: Math.round(needsRedesign ? azaiDevHours * REDESIGN_DEV_MULTIPLIER : azaiDevHours),
      includesRedesign: needsRedesign,
      otherAgencyCost,
      otherAgencyWeeks,
      freelancerCost,
      freelancerWeeks,
      savingsVsOtherAgency: otherAgencyCost - azaiOneTimeTotal,
      savingsVsFreelancer: azaiOneTimeTotal - freelancerCost,
      weeksSavedVsOtherAgency,
      weeksSavedVsFreelancer,
    },
    webflow: {
      annual: webflowAnnual,
      monthly: webflowMonthly,
      isEnterprise: isEnterpriseSize,
    },
    aor: {
      name: aorName,
      monthly: aorMonthly,
      annual: aorAnnual,
      inHouseData,
      inHouseAnnualCost,
      savingsVsInHouse: aorSavingsVsInHouse,
      savingsPercent: aorSavingsPercent,
    },
    roi: {
      yearly: roiYearly,
      points: roiPoints,
      breakEvenMonth,
    },
    time: {
      weeksSavedVsOtherAgency,
      weeksSavedVsFreelancer,
      weeklyOpportunityCost,
      opportunityCostSaved,
    },
    currentCms: {
      name: cms.name,
      category: cms.category,
      annualLicensing: currentLicensing,
      annualHosting: currentHosting,
      annualMaintenance: currentMaintenanceCost,
      totalAnnualCost: currentTotalAnnualCost,
      avgBuildWeeks: currentCmsBuildWeeks,
      changeTime: currentCmsChangeTime,
      maintenanceHoursPerMonth: currentMaintenanceHoursPerMonth,
      devHourlyRate,
    },
    webflowCms: {
      annualLicensing: webflowAnnual,
      annualHosting: 0,
      annualMaintenance: webflowMaintenanceCost,
      totalAnnualCost: webflowTotalAnnual,
      maintenanceHoursPerMonth: webflowMaintenanceHoursPerMonth,
    },
    migration: {
      annualPlatformSavings,
      webflowTotalAnnual,
      webflowChangeTime,
      buildWeeksSaved: currentCmsBuildWeeks - azaiBuildWeeks,
      maintenanceSavings: currentMaintenanceCost - webflowMaintenanceCost,
    },
    totalPages,
    companySize: companySizeData,
    industry: industryData,
  };
}
